import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  TextField,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import { tokens, FLAG_EMOJI_SIZE } from "@/tokens";
import AddIcon from "@mui/icons-material/Add";
import { 
  useCreateScenarioCardMutation, 
  useGetCountrySummaryQuery,
  useGetHTSCodesQuery,
  ScenarioCard, 
  ScenarioCardInput,
} from "@/services/dashboard/sm-api";
import { DateRangeFilter } from "@/components";
import { getCountryFlag } from "@/constants/country-flags";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { getLastYearToDateIsoRange } from "@/utils";

interface CreateScenarioFormProps {
  onScenarioCreated: (scenario: ScenarioCard) => void;
  clientId?: string;
  isAdmin?: boolean;
  tenantId?: string;
}

const CreateScenarioForm: React.FC<CreateScenarioFormProps> = ({ onScenarioCreated, clientId,isAdmin,tenantId }) => {
  const defaultDateRange = useMemo(() => getLastYearToDateIsoRange(), []);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<ScenarioCardInput>>({
    name: "",
    country_list: [],
    rate: 0,
    hts_codes: [],
    dateRange: {
      start: defaultDateRange.start,
      end: defaultDateRange.end,
    },
  });
  const { user } = useAppSelector((state) => state.auth);
  // Ensure clientId is not an empty string
  const validClientId = user?.userRole === roles.TDCLIENT ? user?._id : clientId || "";

  const { data: countrySummary, isLoading: isLoadingCountries } = useGetCountrySummaryQuery(
    { 
      clientId: validClientId ?? "",
      dateFrom: formData.dateRange?.start ? new Date(formData.dateRange.start).toISOString().split('T')[0] : undefined,
      dateTo: formData.dateRange?.end ? new Date(formData.dateRange.end).toISOString().split('T')[0] : undefined
    },
    { skip: !validClientId || !open }
  );

  // Extract all available country codes
  const allCountryCodes = useMemo(() => {
    if (!countrySummary?.getCountryOriginSummary) return [];
    return countrySummary.getCountryOriginSummary.map(country => country.country_code);
  }, [countrySummary]);

  const { data: htsCodes, isLoading: isLoadingHTS } = useGetHTSCodesQuery(
    { 
      clientId: validClientId ?? "",
      // If no countries are selected, send all available countries
      country_codes: formData.country_list?.length ? formData.country_list : allCountryCodes,
      dateFrom: formData.dateRange?.start ? new Date(formData.dateRange.start).toISOString().split('T')[0] : undefined,
      dateTo: formData.dateRange?.end ? new Date(formData.dateRange.end).toISOString().split('T')[0] : undefined
    },
    { skip: !validClientId || !open }
  );

  // Log HTS query parameters and results for debugging
  useEffect(() => {
    if (open) {
      console.log("HTS Query Parameters:", {
        clientId: validClientId,
        country_codes: formData.country_list?.length ? formData.country_list : "All Countries",
        actual_country_codes: formData.country_list?.length ? formData.country_list : allCountryCodes,
        dateFrom: formData.dateRange?.start ? new Date(formData.dateRange.start).toISOString().split('T')[0] : undefined,
        dateTo: formData.dateRange?.end ? new Date(formData.dateRange.end).toISOString().split('T')[0] : undefined
      });
      
      if (htsCodes) {
        console.log("HTS Query Results:", htsCodes);
      }
    }
  }, [validClientId, formData.country_list, formData.dateRange, htsCodes, open, allCountryCodes]);

  const sortedCountries = useMemo(() => {
    if (!countrySummary?.getCountryOriginSummary) return [];
    
    const totalValue = countrySummary.getCountryOriginSummary.reduce(
      (sum, country) => sum + country.total_goods_value, 
      0
    );

    return [...countrySummary.getCountryOriginSummary].sort(
      (a, b) => b.total_goods_value - a.total_goods_value
    ).map(country => ({
      ...country,
      percentage: (country.total_goods_value / totalValue) * 100
    }));
  }, [countrySummary]);

  const sortedHTSCodes = useMemo(() => {
    if (!htsCodes?.getHTSCodesByCountry) return [];
    
    return [...htsCodes.getHTSCodesByCountry].sort(
      (a, b) => b.goods_value - a.goods_value
    );
  }, [htsCodes]);

  // Clear HTS code selections if the selected codes are no longer available
  useEffect(() => {
    if (formData.hts_codes?.length && sortedHTSCodes.length) {
      const availableHTSCodes = sortedHTSCodes.map(code => code.hts_code);
      const validSelectedCodes = formData.hts_codes.filter(code => 
        availableHTSCodes.includes(code)
      );
      
      if (validSelectedCodes.length !== formData.hts_codes.length) {
        console.log("Updating HTS code selections to match available codes");
        setFormData(prev => ({
          ...prev,
          hts_codes: validSelectedCodes
        }));
      }
    } else if (formData.hts_codes?.length && !sortedHTSCodes.length && htsCodes) {
      // If we have HTS codes selected but no sorted HTS codes available,
      // clear the HTS code selections
      console.log("Clearing HTS code selections as no codes are available");
      setFormData(prev => ({
        ...prev,
        hts_codes: []
      }));
    }
  }, [formData.hts_codes, sortedHTSCodes, htsCodes]);

  const [createScenario, { isLoading }] = useCreateScenarioCardMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validClientId) return;

    console.log("Creating scenario with clientId:", validClientId);

    try {
      // If no countries are selected, use all available countries
      const countryList = formData.country_list?.length ? formData.country_list : allCountryCodes;
      
      const scenarioData = {
        ...formData,
        country_list: countryList,
        clientId: validClientId,
        ...(isAdmin && { tenantId: tenantId }),
      } as ScenarioCardInput;

      console.log("Scenario data being sent:", scenarioData);
      
      const result = await createScenario({
        scenarioCard: scenarioData,
      }).unwrap();

      console.log("Scenario creation result:", result);
      
      // Close the dialog and reset form
      setOpen(false);
      setFormData({
        name: "",
        country_list: [],
        rate: 0,
        hts_codes: [],
        dateRange: {
          start: "2000-01-01T00:00:00.000Z",
          end: new Date().toISOString(),
        },
      });
      
      // Call onScenarioCreated with the created scenario
      const createdScenario = {
        _id: result.createScenarioCard.data,
        ...formData,
        country_list: countryList,
        clientId: validClientId,
        tenantId: "", // This will be updated when the list refreshes
      } as ScenarioCard;

      console.log("Created scenario object:", createdScenario);
      
      onScenarioCreated(createdScenario);
    } catch (error) {
      console.error("Error creating scenario:", error);
    }
  };

  // Handle date range selection
  const handleDateRangeChange = (dates: { startDate?: Date; endDate?: Date }) => {
    console.log("Date range changed:", dates);
    
    // When date range changes, we need to update the formData
    const newDateRange = {
      start: dates.startDate ? dates.startDate.toISOString() : defaultDateRange.start,
      end: dates.endDate ? dates.endDate.toISOString() : defaultDateRange.end,
    };
    
    console.log("New date range:", {
      start: newDateRange.start,
      end: newDateRange.end,
      dateFrom: newDateRange.start ? new Date(newDateRange.start).toISOString().split('T')[0] : undefined,
      dateTo: newDateRange.end ? new Date(newDateRange.end).toISOString().split('T')[0] : undefined
    });
    
    // Update the date range without resetting country and HTS selections
    // since the API will now filter the data based on the date range
    setFormData(prev => ({
      ...prev,
      dateRange: newDateRange
    }));
  };

  // Convert ISO string dates to Date objects for DateRangeFilter
  const dateRangeValue = useMemo(() => {
    return {
      startDate: formData.dateRange?.start ? new Date(formData.dateRange.start) : undefined,
      endDate: formData.dateRange?.end ? new Date(formData.dateRange.end) : undefined,
    };
  }, [formData.dateRange]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{
            backgroundColor: tokens.brand.primary,
            color: tokens.bg.surface,
            fontWeight: tokens.fontWeight.bold,
            "&:hover": {
              backgroundColor: tokens.brand.primaryHover,
            },
          }}
        >
          New Scenario
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 2 }}>
            Create New Scenario
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name || ""}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />

            <Autocomplete
              disableCloseOnSelect
              multiple
              options={sortedCountries}
              loading={isLoadingCountries}
              getOptionLabel={(option) => 
                `${option.country_code} (${option.percentage.toFixed(1)}% of goods value)`
              }
              value={sortedCountries.filter(c => 
                formData.country_list?.includes(c.country_code)
              )}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  country_list: newValue.map(v => v.country_code),
                });
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: FLAG_EMOJI_SIZE }}>{getCountryFlag(option.country_code)}</span>
                  <span>{option.country_code}</span>
                  <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                    ({option.percentage.toFixed(1)}% of goods value)
                  </Typography>
                </Box>
              )}
              renderInput={(params) => {
                const updatedParams = {
                  ...params,
                  inputProps: {
                    ...params.inputProps,
                    placeholder: formData.country_list?.length ? "" : "All Countries",
                  }
                };
                return (
                  <TextField
                    {...updatedParams}
                    label="All Countries"
                    helperText="Select countries sorted by goods value"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCountries ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderTags={(value, getTagProps) =>
                value.length === 0 ? (
                  <Box sx={{ color: tokens.text.secondary, pl: 1 }}>All Countries</Box>
                ) : (
                  value.map((option, index) => (
                    <Chip
                      icon={<span style={{ fontSize: FLAG_EMOJI_SIZE, marginLeft: '4px' }}>{getCountryFlag(option.country_code)}</span>}
                      label={`${option.country_code} (${option.percentage.toFixed(1)}%)`}
                      {...getTagProps({ index })}
                      sx={{
                        backgroundColor: tokens.bg.hover,
                        color: tokens.text.excerpt,
                        '& .MuiChip-icon': {
                          fontSize: '20px',
                        }
                      }}
                    />
                  ))
                )
              }
            />

            <TextField
              label="Rate (%)"
              type="number"
              value={formData.rate || ""}
              onChange={(e) =>
                setFormData({ ...formData, rate: parseFloat(e.target.value) })
              }
              required
              fullWidth
              inputProps={{ step: "0.1" }}
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={sortedHTSCodes}
              loading={isLoadingHTS}
              getOptionLabel={(option) => 
                `${option.hts_code} ($${(option.goods_value / 1000000).toFixed(1)}M goods value)`
              }
              value={sortedHTSCodes.filter(c => 
                formData.hts_codes?.includes(c.hts_code)
              )}
              onChange={(_, newValue) => {
                setFormData({
                  ...formData,
                  hts_codes: newValue.map(v => v.hts_code),
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="All HTS Codes"
                  helperText="Select HTS codes sorted by goods value, or paste multiple codes (comma, space, or newline separated)"
                  onPaste={(event) => {
                    event.preventDefault();
                    const pastedText = event.clipboardData.getData('text');
                    
                    // First split by newlines, then by other delimiters if needed
                    const newCodes = pastedText
                      .split(/\r?\n/) // Split by newlines (handles both \n and \r\n)
                      .map(line => line.trim())
                      .filter(line => line.length > 0)
                      .map(line => line.split(/[,\s]+/)) // Split each line by commas or spaces if they exist
                      .flat()
                      .filter(code => code.length > 0)
                      .map(code => code.replace(/\D/g, ""))
                      .filter(code => code.length >= 4);

                    if (newCodes.length > 0) {
                      // Combine existing and new codes, removing duplicates
                      const existingCodes = formData.hts_codes || [];
                      const allCodes = [...new Set([...existingCodes, ...newCodes])];
                      
                      setFormData({
                        ...formData,
                        hts_codes: allCodes,
                      });
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingHTS ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.length === 0 ? (
                  <Box sx={{ color: tokens.text.secondary, pl: 1 }}>All Codes</Box>
                ) : (
                  value.map((option, index) => (
                    <Chip
                      label={`${option.hts_code} ($${(option.goods_value / 1000000).toFixed(1)}M)`}
                      {...getTagProps({ index })}
                      sx={{
                        backgroundColor: tokens.bg.hover,
                        color: tokens.text.excerpt,
                      }}
                    />
                  ))
                )
              }
            />

            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Date Range</Typography>
              <DateRangeFilter 
                value={dateRangeValue}
                onSelectDate={handleDateRangeChange}
              />
              {(isLoadingCountries || isLoadingHTS) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                    Updating data based on selected date range...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button
              onClick={() => setOpen(false)}
              sx={{ color: tokens.text.excerpt }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                backgroundColor: tokens.brand.primary,
                color: tokens.bg.surface,
                fontWeight: tokens.fontWeight.bold,
                "&:hover": {
                  backgroundColor: tokens.brand.primaryHover,
                },
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Create Scenario"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default CreateScenarioForm; 